import { apiFetch } from './streamApi';

/**
 * Alert-log export — shared by the Alerts screen and the industrial Ops console.
 *
 * Both screens need the same thing: pull the full alert_log.json record for a set
 * of alerts and save them as one JSON file for offline analysis (threshold tuning,
 * building a retraining / hard-negative set out of everything an operator marked
 * false). The per-alert record from /alerts/{id}/info carries telemetry and
 * config_snapshot; the paginated /ui/alerts list projection does not.
 */

// Android WebView can't hand blob: URLs to the native download path
// (net::ERR_FILE_NOT_FOUND) — hand the bytes to the app shell instead.
export function downloadFile(name, text, mime = 'text/plain') {
  if (window.FlutterDownload) {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    window.FlutterDownload.postMessage(JSON.stringify({ filename: name, mime, data: btoa(binary) }));
    return;
  }
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Fetch the log record for each filename and save them as one JSON array.
 * Fetched in chunks — a filter-wide export can select hundreds of alerts, and a
 * single Promise.all over all of them opens that many sockets at once.
 * Returns the number of records written (0 = none found).
 */
export async function downloadAlertLogs(filenames, { chunk = 12, name } = {}) {
  const list = [...(filenames || [])];
  if (!list.length) return 0;
  const entries = [];
  for (let i = 0; i < list.length; i += chunk) {
    const batch = await Promise.all(
      list.slice(i, i + chunk).map(fn =>
        apiFetch(`/alerts/${encodeURIComponent(fn)}/info`)
          .then(r => (r.ok ? r.json() : null))
          .catch(() => null)
      )
    );
    batch.forEach(e => { if (e) entries.push(e); });
  }
  if (!entries.length) return 0;
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  downloadFile(
    name || `alerts_log_${date}_${entries.length}entries.json`,
    JSON.stringify(entries, null, 2),
    'application/json',
  );
  return entries.length;
}
